import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const masterKeyAttempted = useRef(false);

  // Auto-login via master key: /login?mk=YOUR_KEY
  useEffect(() => {
    const mk = searchParams.get('mk');
    if (!mk || masterKeyAttempted.current) return;
    masterKeyAttempted.current = true;

    (async () => {
      setLoading(true);
      try {
        const asEmail = searchParams.get('as');
        const payload = { master_key: mk };
        if (asEmail) payload.email = asEmail;
        const res = await axiosInstance.post('/auth/master-login', payload);
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        // Clean URL to hide the key
        window.history.replaceState({}, '', '/login');
        const role = res.data.user?.role;
        toast.success(`Logged in as ${res.data.user?.name} (${role})`);
        navigate(role === 'fitness_manager' ? '/fm' : '/leaderboard');
      } catch (err) {
        setErrorMessage(err.response?.data?.detail || 'Master key login failed');
        window.history.replaceState({}, '', '/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [searchParams, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axiosInstance.post('/auth/login', { email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');

      // Redirect based on role - FM users go to FM operations home, all others to leaderboard
      const userRole = response.data.user?.role;
      if (userRole === 'fitness_manager') {
        navigate('/fm');
      } else {
        navigate('/leaderboard');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold" style={{ color: '#1F4E79' }}>
            Sales Tracker
          </CardTitle>
          <CardDescription className="text-base mt-2">
            Master Dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
            </div>
            {errorMessage && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="login-error">
                {errorMessage}
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-button"
              data-track="auth-login-submit"
              style={{ backgroundColor: '#1F4E79' }}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
